import React, { useEffect, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import api, { formatApiError, formatCurrency } from "../lib/api";
import { PageHeader, EmptyState } from "../components/Shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { Plus, Trash, Printer, Eye, FilePdf, ArrowLeft, X, Warning, PaperPlaneTilt } from "@phosphor-icons/react";
import { buildQuotePdf } from "../lib/quotePdf";
import { ShareQuoteDialog } from "../components/ShareQuoteDialog";
import { PaymentOptions, QuoteStatusSelect } from "../components/QuoteExtras";
import { fetchLogo } from "../components/LogoUploader";
import { useAuth } from "../lib/auth";
import { useProfession, cap, singularProc } from "../lib/professions";

export function QuotesListPage() {
  const prof = useProfession();
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  const load = () => {
    setLoading(true);
    api.get("/quotes").then((r) => setQuotes(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
  };
  const setStatus = async (id, status) => {
    try {
      await api.patch(`/quotes/${id}/status`, { status });
      setQuotes((qs) => qs.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Orçamento marcado como ${{ pending: "pendente", approved: "aprovado", lost: "perdido" }[status]}`);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(load, []);

  const del = async (id) => {
    if (!window.confirm("Excluir orçamento?")) return;
    try {
      await api.delete(`/quotes/${id}`);
      toast.success("Excluído");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div data-testid="quotes-page">
      <PageHeader
        title="Orçamentos"
        subtitle={`Gere orçamentos para seus ${prof.client}s com visualização estilo documento e exportação em PDF.`}
        action={
          <Button onClick={() => navigate("/app/quotes/new")} className="bg-indigo-600 hover:bg-indigo-700" data-testid="quotes-add">
            <Plus size={16} className="mr-1" weight="bold" /> Novo orçamento
          </Button>
        }
      />
      {loading ? <div className="text-slate-500">Carregando...</div>
        : quotes.length === 0 ? (
          <EmptyState
            title="Nenhum orçamento ainda"
            description={`Crie um orçamento com base nos ${prof.procedures.toLowerCase()} cadastrados.`}
            action={<Button onClick={() => navigate("/app/quotes/new")} className="bg-indigo-600 hover:bg-indigo-700">Novo orçamento</Button>}
          />
        ) : (
          <div className="card-crisp overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr className="text-left text-slate-600">
                  <th className="px-5 py-3 font-medium">Número</th>
                  <th className="px-5 py-3 font-medium">{cap(prof.client)}</th>
                  <th className="px-5 py-3 font-medium">Itens</th>
                  <th className="px-5 py-3 font-medium">Total</th>
                  <th className="px-5 py-3 font-medium">Status</th>
                  <th className="px-5 py-3 font-medium">Data</th>
                  <th className="px-5 py-3 w-28" />
                </tr>
              </thead>
              <tbody>
                {quotes.map((q) => (
                  <tr key={q.id} className="border-t border-slate-100 hover:bg-slate-50/50">
                    <td className="px-5 py-3 font-mono-nums">{q.quote_number}</td>
                    <td className="px-5 py-3">{q.patient?.name}</td>
                    <td className="px-5 py-3">{q.items?.length}</td>
                    <td className="px-5 py-3 font-mono-nums font-semibold">
                      {formatCurrency(q.total)}
                      {q.has_margin_alert && <span title="Itens abaixo da margem mínima" className="ml-2 inline-flex items-center text-rose-600 align-middle" data-testid={`quote-alert-${q.id}`}><Warning size={14} weight="fill" /></span>}
                    </td>
                    <td className="px-5 py-3"><QuoteStatusSelect value={q.status} onChange={(v) => setStatus(q.id, v)} testId={`quote-status-${q.id}`} /></td>
                    <td className="px-5 py-3 text-slate-500">{new Date(q.created_at).toLocaleDateString("pt-BR")}</td>
                    <td className="px-5 py-3 text-right">
                      <Link to={`/app/quotes/${q.id}`} className="text-slate-500 hover:text-indigo-600 mr-2 inline-block" data-testid={`quote-view-${q.id}`}>
                        <Eye size={16} />
                      </Link>
                      <button onClick={() => del(q.id)} className="text-slate-500 hover:text-rose-600" data-testid={`quote-delete-${q.id}`}>
                        <Trash size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
    </div>
  );
}

export function QuoteNewPage() {
  const prof = useProfession();
  const [procedures, setProcedures] = useState([]);
  const [patient, setPatient] = useState({ name: "", phone: "", email: "" });
  const [items, setItems] = useState([]);
  const [discount, setDiscount] = useState(0);
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [paymentFees, setPaymentFees] = useState([]);
  const [paymentMethod, setPaymentMethod] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    Promise.all([api.get("/procedures"), api.get("/settings")]).then(([p, s]) => {
      setProcedures(p.data);
      const fees = s.data.payment_fees || [];
      setPaymentFees(fees);
      setPaymentMethod(fees.find((f) => f.is_default)?.name || fees[0]?.name || "");
    }).catch((e) => toast.error(formatApiError(e)));
  }, []);

  const priceFor = (proc, type) => {
    if (!proc) return 0;
    const byPay = proc.pricing.prices_by_payment?.find((f) => f.name === paymentMethod);
    return (byPay ? byPay[`price_${type}`] : proc.pricing[`price_${type}`]) || 0;
  };

  const addItem = () => {
    setItems([...items, { procedure_id: "", price_type: "ideal", quantity: 1, custom_price: 0 }]);
  };
  const updateItem = (idx, key, val) => {
    const list = [...items];
    list[idx] = { ...list[idx], [key]: val };
    setItems(list);
  };
  const removeItem = (idx) => setItems(items.filter((_, i) => i !== idx));

  const getLineTotal = (it) => {
    const proc = procedures.find((p) => p.id === it.procedure_id);
    if (!proc) return 0;
    const price = it.price_type === "custom"
      ? parseFloat(it.custom_price || 0)
      : priceFor(proc, it.price_type);
    return price * (parseInt(it.quantity) || 1);
  };
  const subtotal = items.reduce((s, it) => s + getLineTotal(it), 0);
  const discountAmount = subtotal * ((parseFloat(discount) || 0) / 100);
  const total = subtotal - discountAmount;

  const unitPrice = (it) => (it.price_type === "custom" ? parseFloat(it.custom_price || 0) : priceFor(procedures.find((p) => p.id === it.procedure_id), it.price_type));
  const isBelowMin = (it) => {
    const proc = procedures.find((p) => p.id === it.procedure_id);
    if (!proc) return false;
    return unitPrice(it) * (1 - (parseFloat(discount) || 0) / 100) < priceFor(proc, "minimum") - 0.005;
  };
  const alerts = items.filter(isBelowMin);

  const save = async () => {
    if (!patient.name) { toast.error(`Informe o nome do ${prof.client}`); return; }
    if (items.length === 0 || !items.some((i) => i.procedure_id)) { toast.error("Adicione pelo menos um procedimento"); return; }
    setSaving(true);
    try {
      const payload = {
        patient,
        procedures: items.filter((i) => i.procedure_id).map((i) => ({
          procedure_id: i.procedure_id,
          price_type: i.price_type,
          custom_price: i.price_type === "custom" ? parseFloat(i.custom_price || 0) : null,
          quantity: parseInt(i.quantity) || 1,
        })),
        discount_percentage: parseFloat(discount) || 0,
        notes,
        payment_method: paymentMethod || null,
      };
      const r = await api.post("/quotes", payload);
      toast.success("Orçamento criado!");
      navigate(`/app/quotes/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="quote-new-page">
      <PageHeader
        title="Novo orçamento"
        subtitle={`Monte o orçamento combinando ${prof.procedures.toLowerCase()} cadastrados.`}
        action={
          <Button variant="outline" onClick={() => navigate("/app/quotes")}><ArrowLeft size={16} className="mr-1" />Voltar</Button>
        }
      />

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <div className="card-crisp p-5">
            <h3 className="font-heading font-semibold text-lg mb-4">Dados do {prof.client}</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Label>Nome *</Label><Input value={patient.name} onChange={(e) => setPatient({ ...patient, name: e.target.value })} data-testid="quote-patient-name" className="mt-1" /></div>
              <div><Label>Telefone</Label><Input value={patient.phone} onChange={(e) => setPatient({ ...patient, phone: e.target.value })} data-testid="quote-patient-phone" className="mt-1" /></div>
              <div className="sm:col-span-2"><Label>E-mail</Label><Input type="email" value={patient.email} onChange={(e) => setPatient({ ...patient, email: e.target.value })} data-testid="quote-patient-email" className="mt-1" /></div>
            </div>
          </div>

          <div className="card-crisp p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading font-semibold text-lg">{prof.procedures}</h3>
              <Button type="button" size="sm" onClick={addItem} className="bg-indigo-600 hover:bg-indigo-700" data-testid="quote-add-item"><Plus size={14} className="mr-1" />Adicionar</Button>
            </div>
            {items.length === 0 && <p className="text-sm text-slate-500">Nenhum item adicionado.</p>}
            <div className="space-y-3">
              {items.map((it, idx) => {
                const proc = procedures.find((p) => p.id === it.procedure_id);
                return (
                  <div key={idx} className="border border-slate-200 rounded-md p-3 space-y-3">
                    <div className="flex gap-2 items-start">
                      <Select value={it.procedure_id || "pick"} onValueChange={(v) => updateItem(idx, "procedure_id", v === "pick" ? "" : v)}>
                        <SelectTrigger className="flex-1" data-testid={`quote-item-procedure-${idx}`}><SelectValue placeholder="Selecione o procedimento" /></SelectTrigger>
                        <SelectContent>
                          {procedures.length === 0 && <SelectItem value="pick">Nenhum cadastrado</SelectItem>}
                          {procedures.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      <button onClick={() => removeItem(idx)} className="p-2 text-slate-500 hover:text-rose-600"><X size={16} /></button>
                    </div>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                      <div><Label className="text-xs">Tipo de preço</Label>
                        <Select value={it.price_type} onValueChange={(v) => updateItem(idx, "price_type", v)}>
                          <SelectTrigger data-testid={`quote-item-type-${idx}`}><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="minimum">Mínimo</SelectItem>
                            <SelectItem value="approximate">Aproximado</SelectItem>
                            <SelectItem value="ideal">Ideal</SelectItem>
                            <SelectItem value="custom">Personalizado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      {it.price_type === "custom" && (
                        <div><Label className="text-xs">Preço (R$)</Label>
                          <Input type="number" step="0.01" value={it.custom_price} onChange={(e) => updateItem(idx, "custom_price", e.target.value)} />
                        </div>
                      )}
                      <div><Label className="text-xs">Qtd.</Label>
                        <Input type="number" min="1" value={it.quantity} onChange={(e) => updateItem(idx, "quantity", e.target.value)} />
                      </div>
                      {proc && it.price_type !== "custom" && (
                        <div><Label className="text-xs">Preço unitário</Label>
                          <div className="h-10 flex items-center font-mono-nums font-semibold">{formatCurrency(priceFor(proc, it.price_type))}</div>
                        </div>
                      )}
                      <div><Label className="text-xs">Subtotal</Label>
                        <div className="h-10 flex items-center font-mono-nums font-bold text-indigo-700">{formatCurrency(getLineTotal(it))}</div>
                      </div>
                    </div>
                    {isBelowMin(it) && (
                      <div className="flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid={`quote-item-alert-${idx}`}>
                        <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                        <span>Abaixo da margem mínima: o preço mínimo deste procedimento{paymentMethod ? ` no ${paymentMethod}` : ""} é <strong className="font-mono-nums">{formatCurrency(priceFor(proc, "minimum"))}</strong>{(parseFloat(discount) || 0) > 0 ? " (considerando o desconto)" : ""}.</span>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="card-crisp p-5">
            <h3 className="font-heading font-semibold text-lg mb-4">Observações</h3>
            <Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="quote-notes" placeholder="Condições, formas de pagamento, validade, etc." />
          </div>
        </div>

        <div className="lg:col-span-1">
          <div className="card-crisp p-5 sticky top-20">
            <h3 className="font-heading font-semibold text-lg mb-4">Resumo</h3>
            <div className="space-y-3 text-sm">
              {paymentFees.length > 0 && (
                <div>
                  <Label className="text-xs">Forma de pagamento</Label>
                  <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                    <SelectTrigger className="mt-1" data-testid="quote-payment-method"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {paymentFees.map((f) => <SelectItem key={f.name} value={f.name}>{f.name} ({f.percentage}%)</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Os preços são recalculados com a taxa desta forma de pagamento.</p>
                </div>
              )}
              <div className="flex justify-between"><span>Subtotal</span><span className="font-mono-nums font-semibold">{formatCurrency(subtotal)}</span></div>
              <div>
                <Label className="text-xs">Desconto (%)</Label>
                <Input type="number" step="0.01" value={discount} onChange={(e) => setDiscount(e.target.value)} data-testid="quote-discount" className="mt-1" />
              </div>
              <div className="flex justify-between text-rose-600"><span>-Desconto</span><span className="font-mono-nums">{formatCurrency(discountAmount)}</span></div>
              <div className="pt-3 border-t border-slate-200 flex justify-between items-baseline">
                <span className="font-semibold">Total</span>
                <span className="font-mono-nums font-extrabold text-2xl text-indigo-700">{formatCurrency(total)}</span>
              </div>
            </div>
            {alerts.length > 0 && (
              <div className="mt-4 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2" data-testid="quote-margin-alert">
                <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                <span><strong>{alerts.length}</strong> item(ns) abaixo da margem mínima. Você pode gerar mesmo assim, mas o lucro ficará comprometido.</span>
              </div>
            )}
            <Button onClick={save} disabled={saving} className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700" data-testid="quote-save">
              {saving ? "Salvando..." : "Gerar orçamento"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function QuoteViewPage() {
  const { id } = useParams();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const prof = useProfession();

  const [logo, setLogo] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);

  useEffect(() => {
    api.get(`/quotes/${id}`).then((r) => setQuote(r.data))
      .catch((e) => toast.error(formatApiError(e)))
      .finally(() => setLoading(false));
    fetchLogo().then(setLogo);
  }, [id]);

  const exportPdf = () => { if (quote) { buildQuotePdf(quote, logo, user?.clinic_name); toast.success("PDF exportado!"); } };

  if (loading) return <div className="text-slate-500">Carregando...</div>;
  if (!quote) return <div>Não encontrado.</div>;

  return (
    <div data-testid="quote-view-page">
      <PageHeader
        title={`Orçamento ${quote.quote_number}`}
        subtitle="Visualização estilo documento. Exporte em PDF ou imprima."
        action={
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => window.print()} data-testid="quote-print"><Printer size={16} className="mr-1" />Imprimir</Button>
            <QuoteStatusSelect value={quote.status} onChange={async (v) => { try { await api.patch(`/quotes/${quote.id}/status`, { status: v }); setQuote({ ...quote, status: v }); toast.success("Status atualizado"); } catch (e) { toast.error(formatApiError(e)); } }} testId="quote-view-status" />
            <Button onClick={() => setShareOpen(true)} variant="outline" className="border-emerald-300 text-emerald-700 hover:bg-emerald-50" data-testid="quote-share"><PaperPlaneTilt size={16} className="mr-1" />Enviar ao {prof.client}</Button>
            <Button onClick={exportPdf} className="bg-indigo-600 hover:bg-indigo-700" data-testid="quote-export-pdf"><FilePdf size={16} className="mr-1" />Exportar PDF</Button>
          </div>
        }
      />
      {quote && <ShareQuoteDialog quote={quote} open={shareOpen} onOpenChange={setShareOpen} />}

      <div className="bg-slate-200 p-6 lg:p-10 rounded-lg">
        <div className="bg-white max-w-3xl mx-auto p-10 shadow-xl" id="quote-paper">
          <div className="flex items-start justify-between pb-6 border-b border-slate-200">
            <div>
              <div className="font-heading font-extrabold text-2xl text-slate-900">{quote.clinic_name || user?.clinic_name || "Minha Clínica"}</div>
              <div className="text-xs text-slate-500 mt-1 uppercase tracking-wider">Orçamento · {prof.tagline.replace("Precificação para ", "")}</div>
            </div>
            <div className="text-right text-sm">
              {logo && <img src={logo.dataUrl} alt="Logo da clínica" className="max-h-14 max-w-[160px] object-contain ml-auto mb-2" data-testid="quote-logo" />}
              <div className="font-mono-nums text-slate-900 font-semibold">Nº {quote.quote_number}</div>
              <div className="text-slate-500">{new Date(quote.created_at).toLocaleDateString("pt-BR")}</div>
            </div>
          </div>

          <div className="py-6">
            <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">{cap(prof.client)}</div>
            <div className="text-slate-900 font-semibold">{quote.patient.name}</div>
            {quote.patient.phone && <div className="text-sm text-slate-600">{quote.patient.phone}</div>}
            {quote.patient.email && <div className="text-sm text-slate-600">{quote.patient.email}</div>}
          </div>

          <table className="w-full text-sm border-t border-slate-200">
            <thead>
              <tr className="text-left text-xs text-slate-500 uppercase tracking-wider">
                <th className="py-3">{singularProc(prof)}</th>
                <th className="py-3 text-center">Qtd</th>
                <th className="py-3 text-right">Unitário</th>
                <th className="py-3 text-right">Total</th>
              </tr>
            </thead>
            <tbody>
              {quote.items.map((it, i) => (
                <tr key={i} className="border-t border-slate-100">
                  <td className="py-3">{it.name}</td>
                  <td className="py-3 text-center font-mono-nums">{it.quantity}</td>
                  <td className="py-3 text-right font-mono-nums">{formatCurrency(it.unit_price)}</td>
                  <td className="py-3 text-right font-mono-nums font-semibold">{formatCurrency(it.line_total)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          <div className="mt-6 pt-6 border-t border-slate-200 space-y-2 text-sm">
            <div className="flex justify-between"><span className="text-slate-600">Subtotal</span><span className="font-mono-nums">{formatCurrency(quote.subtotal)}</span></div>
            {quote.discount_amount > 0 && (
              <div className="flex justify-between text-rose-600"><span>Desconto ({quote.discount_percentage}%)</span><span className="font-mono-nums">- {formatCurrency(quote.discount_amount)}</span></div>
            )}
            <div className="flex justify-between items-baseline pt-3 border-t border-slate-200">
              <span className="font-heading font-bold text-lg">Total</span>
              <span className="font-mono-nums font-extrabold text-3xl text-indigo-700">{formatCurrency(quote.total)}</span>
            </div>
            {quote.payment_method && <div className="text-right text-xs text-slate-500" data-testid="quote-view-payment">Forma de pagamento: <strong>{quote.payment_method}</strong></div>}
          </div>
          <PaymentOptions options={quote.payment_options} testId="quote-view-payment-options" />
          {quote.has_margin_alert && (
            <div className="mt-4 flex items-start gap-2 text-xs text-rose-700 bg-rose-50 border border-rose-200 rounded-md px-3 py-2 print:hidden" data-testid="quote-view-alert">
              <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
              <span>Atenção (visível só para você): {quote.items.filter((i) => i.below_minimum).map((i) => i.name).join(", ")} abaixo da margem mínima. Não aparece no PDF.</span>
            </div>
          )}

          {quote.notes && (
            <div className="mt-8 pt-6 border-t border-slate-200">
              <div className="text-xs text-slate-500 uppercase tracking-wider mb-2">Observações</div>
              <div className="text-sm text-slate-700 whitespace-pre-wrap">{quote.notes}</div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
